import { useState } from "react";
import { useAuth } from "@lib/auth-client";
import {
  useAdminProducts,
  useCreateProductMutation,
  useTriggerEnrichmentMutation
} from "@lib/hooks";

export function AdminPage() {
  const { user, loading, isAdmin } = useAuth();
  const [form, setForm] = useState({ name: "", brand: "", model: "", category: "" });
  const productsQuery = useAdminProducts();
  const createProduct = useCreateProductMutation();
  const triggerEnrichment = useTriggerEnrichmentMutation();
  const [message, setMessage] = useState<string | null>(null);

  if (loading) {
    return <p>Loading account…</p>;
  }

  if (!user || !isAdmin) {
    return <p>You must be an administrator to access this page.</p>;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);
    try {
      await createProduct.mutateAsync(form);
      setForm({ name: "", brand: "", model: "", category: "" });
      await triggerEnrichment.mutateAsync();
      setMessage("Product queued and enrichment triggered.");
    } catch (error) {
      console.error("Product creation failed", error);
      setMessage((error as Error)?.message ?? "Unable to create product");
    }
  };

  return (
    <div className="page admin">
      <h1>Admin Panel</h1>
      <form className="admin-form" onSubmit={handleSubmit}>
        <h2>Add a product</h2>
        <div className="form-grid">
          <label>
            Name
            <input name="name" value={form.name} onChange={handleChange} required minLength={2} />
          </label>
          <label>
            Brand
            <input name="brand" value={form.brand} onChange={handleChange} required minLength={1} />
          </label>
          <label>
            Model
            <input name="model" value={form.model} onChange={handleChange} required minLength={1} />
          </label>
          <label>
            Category
            <input name="category" value={form.category} onChange={handleChange} required minLength={1} />
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" disabled={createProduct.isLoading || triggerEnrichment.isLoading}>
            {createProduct.isLoading || triggerEnrichment.isLoading ? "Processing…" : "Create product"}
          </button>
          <button
            type="button"
            onClick={() => void triggerEnrichment.mutateAsync()}
            disabled={triggerEnrichment.isLoading}
          >
            {triggerEnrichment.isLoading ? "Running enrichment…" : "Run enrichment"}
          </button>
        </div>
        {message ? <p>{message}</p> : null}
        {createProduct.isError ? <p className="error">{(createProduct.error as Error)?.message}</p> : null}
        {triggerEnrichment.isError ? (
          <p className="error">{(triggerEnrichment.error as Error)?.message}</p>
        ) : null}
      </form>

      <section className="admin-products">
        <h2>All products</h2>
        {productsQuery.isLoading ? <p>Loading products…</p> : null}
        {productsQuery.isError ? <p>Unable to load products.</p> : null}
        {productsQuery.data ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Brand</th>
                <th>Model</th>
                <th>Category</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {productsQuery.data.map((product) => (
                <tr key={product.id}>
                  <td>{product.name}</td>
                  <td>{product.brand}</td>
                  <td>{product.model}</td>
                  <td>{product.category}</td>
                  <td>{product.status}</td>
                  <td>{product.updated_at ? new Date(product.updated_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </div>
  );
}
